import React, { useCallback, useEffect, useMemo, useState } from 'react';
import userService, { InvitationCodeRow } from '../../../../shared/services/userService';

const InvitationCodesContent: React.FC = () => {
  const [rows, setRows] = useState<InvitationCodeRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<number>(1);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await userService.getInvitationCodes();
      if (res.success) {
        setRows(res.data || []);
      }
    } catch (err: any) {
      setError(err?.message || "Impossible de charger les codes d'invitation.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const availableCount = useMemo(
    () => rows.filter((r) => Number(r.used) !== 1).length,
    [rows]
  );

  const handleGenerate = useCallback(async () => {
    if (creating) return;
    setCreating(true);
    setError(null);
    try {
      const safeQuantity = Math.max(1, Math.min(20, Math.floor(Number(quantity) || 1)));
      const res = await userService.generateInvitationCodes({ quantity: safeQuantity });
      if (res.success) {
        await loadRows();
      }
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de la génération des codes.');
    } finally {
      setCreating(false);
    }
  }, [creating, loadRows, quantity]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-cockpit-text">Codes d&apos;invitation</h3>
          <p className="text-sm text-cockpit-text-2">
            Visualisez tous les codes actuels et générez de nouveaux codes.
          </p>
          <p className="text-xs text-[#c7d2fe] mt-1">
            Total: {rows.length} · Disponibles: {availableCount} · Utilisés: {Math.max(0, rows.length - availableCount)}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={20}
            value={quantity}
            onChange={(e) => setQuantity(Number(e.target.value))}
            className="w-20 rounded-lg border border-[#223046] bg-[#0b1220] px-2 py-1 text-sm text-cockpit-text outline-none focus:border-[#6366f1]/60"
            title="Nombre de codes à générer (1 à 20)"
          />
          <button
            type="button"
            onClick={handleGenerate}
            disabled={creating}
            className="rounded-lg border border-emerald-400/40 bg-emerald-500/20 px-3 py-1.5 text-sm font-semibold text-emerald-100 transition-colors hover:bg-emerald-500/30 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {creating ? 'Génération...' : 'Générer'}
          </button>
          <button
            type="button"
            onClick={loadRows}
            disabled={loading}
            className="rounded-lg border border-cyan-400/30 bg-cyan-500/10 px-3 py-1.5 text-sm text-cyan-100 transition-colors hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? 'Chargement...' : 'Actualiser'}
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-500/40 bg-red-900/20 px-3 py-2 text-sm text-red-200">
          {error}
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-[#223046]/90">
        <table className="min-w-full divide-y divide-[#1a2537] text-sm">
          <thead className="bg-[#0b1220]/80">
            <tr className="text-left">
              <th className="px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Code</th>
              <th className="px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Statut</th>
              <th className="px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Créé le</th>
              <th className="px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Créé par</th>
              <th className="px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Utilisé par</th>
              <th className="px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Utilisé le</th>
              <th className="px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Expire le</th>
              <th className="px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1a2537]/60">
            {!loading && rows.length < 1 ? (
              <tr>
                <td className="px-3 py-4 text-cockpit-text-2" colSpan={8}>
                  Aucun code d&apos;invitation trouvé.
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const used = Number(r.used) === 1;
                return (
                  <tr key={r.id} className="hover:bg-[#0b1220]/40 transition-colors">
                    <td className="px-3 py-2 font-mono text-xs text-cockpit-text">{r.code}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded px-2 py-0.5 text-xs font-semibold border ${
                          used
                            ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                            : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                        }`}
                      >
                        {used ? 'Utilisé' : 'Disponible'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-cockpit-text-2">
                      {r.created_at ? new Date(r.created_at).toLocaleString('fr-FR') : '-'}
                    </td>
                    <td className="px-3 py-2 text-xs text-cockpit-text-2">{r.created_by_username || '-'}</td>
                    <td className="px-3 py-2 text-xs text-cockpit-text-2">{r.used_by_username || '-'}</td>
                    <td className="px-3 py-2 text-xs text-cockpit-text-2">
                      {r.used_at ? new Date(r.used_at).toLocaleString('fr-FR') : '-'}
                    </td>
                    <td className="px-3 py-2 text-xs text-cockpit-text-2">
                      {r.expires_at ? new Date(r.expires_at).toLocaleString('fr-FR') : '-'}
                    </td>
                    <td className="px-3 py-2 text-xs text-cockpit-text-2">{r.notes || '-'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvitationCodesContent;
